import React, { useMemo, useEffect, useState, useRef } from 'react';
import MedicalPassportField from '../../components/MedicalPassportField';
import { useValue } from '../../ValueContext';
import createReducer from '../../../../../../questionnaire/reducer';
import createSelector from '../../../../../../questionnaire/selector';
import { format } from 'date-fns';
import { capitalize } from 'lodash';

// Reducer to parse and display questionnaire data
const clinicalReducer = createReducer({
  Sections: sections => null,
  Object: fields => {
    const visibleFields = fields.filter(field => field.child);
    return visibleFields.length === 0 ? null : visibleFields.length === 1 ? (
      visibleFields[0].child
    ) : (
      <ul style={{ margin: 0, paddingLeft: 0, listStyleType: 'none' }}>
        {visibleFields.map((field, i) => (
          <li key={i}>{field.child}</li>
        ))}
      </ul>
    );
  },
  Text: (label, text) => {
    if (!text) return null;
    const cleanLabel = label.replace(/\?/g, '').replace(/:+$/, '').trim();
    return cleanLabel ? (
      <div>
        {cleanLabel}: {text}
      </div>
    ) : (
      <div>{text}</div>
    );
  },
  LongText: (label, text) => {
    if (!text) return null;
    const cleanLabel = label.replace(/\?/g, '').replace(/:+$/, '').trim();
    return cleanLabel ? (
      <div>
        {cleanLabel}: {text}
      </div>
    ) : (
      <div>{text}</div>
    );
  },
  Checkboxes: (label, checkboxes) => {
    const visibleCheckboxes = checkboxes.filter(checkbox => checkbox.checked);

    const cleanLabel = label => {
      return label
        .replace(/\?/g, '')
        .replace(/^Were you born with\s*/i, 'Born with ')
        .replace(/^Were you\s*/i, '')
        .replace(/^Do you\s*/i, '')
        .replace(/^Have you\s*/i, '')
        .replace(/^Are you\s*/i, '');
    };

    return visibleCheckboxes.length === 0 ? null : visibleCheckboxes.length === 1 ? (
      <div>
        <div
          style={
            visibleCheckboxes[0].label.toLowerCase() === 'allergic to latex?' ||
            visibleCheckboxes[0].label.toLowerCase() === 'allergic to latex'
              ? { color: 'red' }
              : {}
          }
        >
          {cleanLabel(visibleCheckboxes[0].label)}
        </div>
        {visibleCheckboxes[0].child && <div style={{ marginLeft: '1rem' }}>{visibleCheckboxes[0].child}</div>}
      </div>
    ) : (
      <ul style={{ margin: 0, paddingLeft: 0, listStyleType: 'none' }}>
        {visibleCheckboxes.map((checkbox, i) => (
          <li key={i}>
            <div
              style={
                checkbox.label.toLowerCase() === 'allergic to latex?' ||
                checkbox.label.toLowerCase() === 'allergic to latex'
                  ? { color: 'red' }
                  : {}
              }
            >
              {cleanLabel(checkbox.label)}
            </div>
            {checkbox.child && <div style={{ marginLeft: '1rem' }}>{checkbox.child}</div>}
          </li>
        ))}
      </ul>
    );
  },
  List: (label, children) => {
    // Filter out the questionnaire question itself
    const visibleChildren = children.filter(child => {
      if (typeof child === 'string' && /Have you|Do you|Are you/i.test(child)) {
        return false;
      }
      return child;
    });
    return visibleChildren.length === 0 ? null : visibleChildren.length === 1 ? (
      visibleChildren[0]
    ) : (
      <ul style={{ margin: 0, paddingLeft: 0, listStyleType: 'none' }}>
        {visibleChildren.map((child, i) => (
          <li key={i} style={{ breakInside: 'avoid' }}>
            {child}
          </li>
        ))}
      </ul>
    );
  },
  ListItem: fields => {
    const visibleFields = fields.filter(field => field.child);
    if (visibleFields.length === 0) return null;

    const sortedFields = [...visibleFields].sort((a, b) => {
      const aLabel = a.label?.toLowerCase() || '';
      const bLabel = b.label?.toLowerCase() || '';

      const aIsMedication = aLabel.includes('medication') || aLabel.includes('drug') || aLabel.includes('substance');
      const bIsMedication = bLabel.includes('medication') || bLabel.includes('drug') || bLabel.includes('substance');

      const aIsReaction = aLabel.includes('reaction') || aLabel.includes('symptom');
      const bIsReaction = bLabel.includes('reaction') || bLabel.includes('symptom');

      if (aIsMedication && !bIsMedication) return -1;
      if (!aIsMedication && bIsMedication) return 1;
      if (aIsReaction && !bIsReaction) return 1;
      if (!aIsReaction && bIsReaction) return -1;

      return 0;
    });

    return (
      <div style={{ breakInside: 'avoid', marginBottom: '0.5rem' }}>
        {sortedFields.map((field, i) => (
          <div key={i}>{field.child}</div>
        ))}
      </div>
    );
  },
  YesNo: (question, positive, child) => {
    if (!positive) return null;

    // Skip rendering the question for Prior Surgeries
    if (/prior surgery|with the use of sedation/i.test(question)) {
      return child || null;
    }

    const transformedQuestion = capitalize(
      question
        .replace(/\?/g, '')
        .replace(/^Were you born with\s*/i, 'Born with ')
        .replace(/^Were you\s*/i, '')
        .replace(/^Do you have\s*/g, '')
        .replace(/^Do you\s*/g, '')
        .replace(/^Have you\s*/g, '')
        .replace(/^Are you\s*/g, '')
    );
    return (
      <div>
        <div style={child ? { fontWeight: 'bold' } : {}}>{transformedQuestion}</div>
        {child ? <div style={{ marginLeft: '1rem' }}>{child}</div> : null}
      </div>
    );
  },
  OneOf: (label, options, answer) => {
    if (!answer) return null;
    const cleanLabel = label.replace(/\?/g, '').replace(/:+$/, '').trim();
    return cleanLabel ? (
      <div>
        <div style={answer.child ? { fontWeight: 'bold' } : {}}>
          {cleanLabel}: {answer.label}
        </div>
        {answer.child && <div style={{ marginLeft: '1rem' }}>{answer.child}</div>}
      </div>
    ) : (
      <div>
        <div style={answer.child ? { fontWeight: 'bold' } : {}}>{answer.label}</div>
        {answer.child && <div style={{ marginLeft: '1rem' }}>{answer.child}</div>}
      </div>
    );
  },
  Medication: value => (value ? <span>{value}</span> : null),
  BodyMassIndex: (heightFt, heightIn, weightLb) => null,
  DateOfBirth: date => (date ? <span>{format(date, 'MM/DD/YYYY')}</span> : null),
  Date: date => (date ? <span>{format(date, 'MM/DD/YYYY')}</span> : null),
});

const selectors = {
  priorSurgeries: createSelector(['priorSurgeries']),
  allergies: createSelector(['allergies']),
  anesthesia: createSelector(['medicalHistory1']),
  heart: createSelector(['medicalHistory2']),
  lungs: createSelector(['medicalHistory3']),
  generalHealth: createSelector(['medicalHistory4']),
  activityLevel: createSelector(['medicalHistory5']),
  lifestyle: createSelector(['medicalHistory6']),
  preExisting: createSelector(['Pre-Existing']),
};

const calculateBMI = (weight, heightFeet, heightInches) => {
  const totalInches = heightFeet * 12 + heightInches;
  if (!weight || !totalInches) return null;
  return ((weight / (totalInches * totalInches)) * 703).toFixed(1);
};

const Page1 = ({ hospitalName, defaultValue, lastPreOpCompletedEvent, scheduleProvider }) => {
  const { questionnaire, questionnaireAnswers } = useValue();

  // Parse clinical data from questionnaire
  const clinicalData = useMemo(() => {
    const data = {};
    Object.entries(selectors).forEach(([key, selector]) => {
      const node = selector(questionnaire, questionnaireAnswers);
      data[key] = node && clinicalReducer(...node);
    });
    return data;
  }, [questionnaire, questionnaireAnswers]);

  const heightFt = defaultValue?.demographicInformation?.bmi?.heightFt;
  const heightIn = defaultValue?.demographicInformation?.bmi?.heightIn;
  const weightLb = defaultValue?.demographicInformation?.bmi?.weightLb;
  const bmi = calculateBMI(parseInt(weightLb, 10), parseInt(heightFt, 10), parseInt(heightIn, 10));

  const FT = 30.48;
  const IN = 2.54;
  const LBS = 0.453592;
  const heightCm = heightFt && heightIn ? Math.round(heightFt * FT + heightIn * IN) : null;
  const weightKg = weightLb ? Math.round(weightLb * LBS) : null;

  const pageRef = useRef(null);
  const [pageStyle, setPageStyle] = useState({});

  useEffect(() => {
    const handleResize = () => {
      if (pageRef.current) {
        const pageHeight = pageRef.current.offsetHeight;
        if (pageHeight > 1086) {
          if (pageHeight > 2150) {
            setPageStyle({ minHeight: 2400 });
          } else {
            setPageStyle({ minHeight: 2150 });
          }
        } else {
          setPageStyle({ minHeight: 1085 });
        }
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [clinicalData]);

  return (
    <div id="page1-restore-summary" className="page" style={pageStyle}>
      <div ref={pageRef}>
        {/* Header Section */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'start',
            marginBottom: '1rem',
            pageBreakInside: 'avoid',
          }}
        >
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, marginBottom: '0.3rem' }}>{(hospitalName || '').toUpperCase()}</h3>
            <MedicalPassportField name="procedure.physician.name" label="Surgeon" />
            <MedicalPassportField name="procedure.serviceTime" label="Procedure Date" />
            <MedicalPassportField name="procedure.name" label="Procedure" />
          </div>
          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <div
              style={{
                border: '1px solid black',
                padding: '1rem',
                lineHeight: '25px',
                minWidth: '300px',
              }}
            >
              <MedicalPassportField name="procedure.patient.name" label="Patient" fontWeight={'bold'} fontSize={16} />
              <MedicalPassportField name="procedure.patient.providerId" label="MRN#" />
              <MedicalPassportField name="dob" label="DOB" path={['demographicInformation', 'dob']} />
              <MedicalPassportField name="procedure.serviceTime" label="DOS" />
              <MedicalPassportField name="procedure.physician.name" label="Surgeon" />
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <MedicalPassportField name="procedure.patient.age" label="Age" />
                <MedicalPassportField name="procedure.patient.sex" label="Sex" />
              </div>
            </div>
          </div>
        </div>

        {/* BMI Section */}
        <div style={{ marginBottom: '1rem', fontSize: '10px', pageBreakInside: 'avoid' }}>
          Height: {heightIn && heightFt ? `${heightFt}'${heightIn}'' (${heightCm}cm)` : '-'} Weight:{' '}
          {weightLb ? `${weightLb} lb (${weightKg}kg)` : '-'}{' '}
          <span style={{ color: bmi > 40 ? 'red' : 'inherit' }}>Body Mass Index: {bmi || '-'}</span>
        </div>

        <div style={{ borderTop: '1px solid #ccc', marginBottom: '0.5rem' }} />

        {/* Clinical Summary Sections - All sections in one place with page break control */}
        <div style={{ fontSize: '12px', lineHeight: '1.5' }}>
          <div style={{ marginBottom: '0.5rem', pageBreakInside: 'avoid' }}>
            <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '0.2rem' }}>Prior Surgeries</div>
            <div style={{ columnCount: 3, columnGap: '1.5rem' }}>
              {clinicalData.priorSurgeries || <span style={{ color: '#999' }}>None</span>}
            </div>
          </div>

          <div
            style={{
              borderTop: '1px solid #ddd',
              paddingTop: '0.5rem',
              marginBottom: '0.5rem',
              pageBreakInside: 'avoid',
            }}
          >
            <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '0.2rem' }}>Allergies</div>
            <div style={{ columnCount: 3, columnGap: '1.5rem' }}>
              {clinicalData.allergies || <span style={{ color: '#999' }}>None</span>}
            </div>
          </div>

          <div
            style={{
              borderTop: '1px solid #ddd',
              paddingTop: '0.5rem',
              marginBottom: '0.5rem',
              pageBreakInside: 'avoid',
            }}
          >
            <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '0.2rem' }}>Anesthesia</div>
            <div style={{ columnCount: 3, columnGap: '1.5rem' }}>
              {clinicalData.anesthesia || <span style={{ color: '#999' }}>None</span>}
            </div>
          </div>

          <div
            style={{
              borderTop: '1px solid #ddd',
              paddingTop: '0.5rem',
              marginBottom: '0.5rem',
              pageBreakInside: 'avoid',
            }}
          >
            <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '0.2rem' }}>Heart</div>
            <div style={{ columnCount: 3, columnGap: '1.5rem' }}>
              {clinicalData.heart || <span style={{ color: '#999' }}>None</span>}
            </div>
          </div>

          <div
            style={{
              borderTop: '1px solid #ddd',
              paddingTop: '0.5rem',
              marginBottom: '0.5rem',
              pageBreakInside: 'avoid',
            }}
          >
            <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '0.2rem' }}>Lungs</div>
            <div style={{ columnCount: 3, columnGap: '1.5rem' }}>
              {clinicalData.lungs || <span style={{ color: '#999' }}>None</span>}
            </div>
          </div>

          <div
            style={{
              borderTop: '1px solid #ddd',
              paddingTop: '0.5rem',
              marginBottom: '0.5rem',
              pageBreakInside: 'avoid',
            }}
          >
            <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '0.2rem' }}>General Health</div>
            <div style={{ columnCount: 3, columnGap: '1.5rem' }}>
              {clinicalData.generalHealth || <span style={{ color: '#999' }}>None</span>}
            </div>
          </div>

          <div
            style={{
              borderTop: '1px solid #ddd',
              paddingTop: '0.5rem',
              marginBottom: '0.5rem',
              pageBreakInside: 'avoid',
            }}
          >
            <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '0.2rem' }}>Activity level</div>
            <div style={{ columnCount: 3, columnGap: '1.5rem' }}>
              {clinicalData.activityLevel || <span style={{ color: '#999' }}>None</span>}
            </div>
          </div>

          <div
            style={{
              borderTop: '1px solid #ddd',
              paddingTop: '0.5rem',
              marginBottom: '0.5rem',
              pageBreakInside: 'avoid',
            }}
          >
            <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '0.2rem' }}>Lifestyle</div>
            <div style={{ columnCount: 3, columnGap: '1.5rem' }}>
              {clinicalData.lifestyle || <span style={{ color: '#999' }}>None</span>}
            </div>
          </div>

          <div
            style={{
              borderTop: '1px solid #ddd',
              paddingTop: '0.5rem',
              marginBottom: '0.5rem',
              pageBreakInside: 'avoid',
            }}
          >
            <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '0.2rem' }}>
              Pre-Existing medical conditions not covered above?
            </div>
            <div style={{ columnCount: 3, columnGap: '1.5rem' }}>
              {clinicalData.preExisting || <span style={{ color: '#999' }}>None</span>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Page1;
